import type { UserBillingData } from "../../api/admin-revenue";
import { Td, Th, fmtDate, tableStyles } from "../../components/DataTable";
import { t } from "../../i18n";

export function LedgerTable({
  entries,
}: {
  entries: UserBillingData["token_ledger"];
}): React.JSX.Element {
  return (
    <section style={s.section}>
      <h3 style={s.sectionTitle}>{t("billing_table.token_ledger")}</h3>
      <table style={s.table}>
        <thead>
          <tr>
            <Th>{t("billing_table.col_type")}</Th>
            <Th>{t("billing_table.col_amount")}</Th>
            <Th>{t("billing_table.col_balance_after")}</Th>
            <Th>{t("billing_table.col_reason")}</Th>
            <Th>{t("billing_table.col_date")}</Th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id}>
              <Td>{e.type}</Td>
              <Td>{e.amount}</Td>
              <Td>{e.balance_after}</Td>
              <Td>{e.reason ?? "-"}</Td>
              <Td>{fmtDate(e.created_at)}</Td>
            </tr>
          ))}
          {entries.length === 0 && (
            <tr>
              <Td colSpan={5}>{t("billing_table.no_ledger_entries")}</Td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

export function WebhookTable({
  events,
  onReplay,
}: {
  events: UserBillingData["webhook_events"];
  onReplay: (id: string) => void;
}): React.JSX.Element {
  return (
    <section style={s.section}>
      <h3 style={s.sectionTitle}>{t("billing_table.webhook_events")}</h3>
      <table style={s.table}>
        <thead>
          <tr>
            <Th>{t("billing_table.col_provider")}</Th>
            <Th>{t("billing_table.col_event_type")}</Th>
            <Th>{t("billing_table.col_status")}</Th>
            <Th>{t("billing_table.col_date")}</Th>
            <Th>{t("billing_table.col_action")}</Th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id}>
              <Td>{e.provider}</Td>
              <Td>{e.event_type}</Td>
              <Td>
                <StatusBadge status={e.status} />
              </Td>
              <Td>{fmtDate(e.created_at)}</Td>
              <Td>
                {e.status === "failed" && (
                  <button
                    type="button"
                    onClick={() => onReplay(e.id)}
                    style={s.replayBtn}
                  >
                    Replay
                  </button>
                )}
              </Td>
            </tr>
          ))}
          {events.length === 0 && (
            <tr>
              <Td colSpan={5}>{t("billing_table.no_webhook_events")}</Td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

/* ---------- Internal ---------- */

const STATUS_COLORS: Record<string, React.CSSProperties> = {
  processed: { background: "#dcfce7", color: "#15803d" },
  failed: { background: "#fef2f2", color: "#dc2626" },
  pending: { background: "#fef9c3", color: "#a16207" },
};

function StatusBadge({ status }: { status: string }): React.JSX.Element {
  const color = STATUS_COLORS[status] ?? {};
  return <span style={{ ...s.badge, ...color }}>{status}</span>;
}

/* ---------- Styles ---------- */

const s: Record<string, React.CSSProperties> = {
  ...tableStyles,
  section: { marginBottom: 24 },
  sectionTitle: { fontSize: 15, fontWeight: 600, marginBottom: 8 },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 500,
  },
  replayBtn: {
    padding: "4px 10px",
    fontSize: 12,
    borderRadius: 4,
    border: "1px solid var(--color-danger)",
    color: "var(--color-danger)",
  },
};
